<template>
  <!-- Panels swapped: conversation list middle, message detail right -->
  <div class="conversations-container" :class="{ 'is-resizing': isResizing, 'is-resizing-right': isResizingRightPanel, 'right-panel-visible': shouldShowRightPanel && showRightPanel }">
    <!-- Overlay for force-shown panels -->
    <div
      v-if="forceShowLeftSidebar || forceShowRightPanel"
      class="panel-overlay"
      @click="closeForcedPanels"
    ></div>

    <!-- Floating button to show left sidebar when folded -->
    <el-button
      v-if="isLeftSidebarHiddenByResponsive && !forceShowLeftSidebar"
      class="floating-sidebar-toggle left"
      icon="el-icon-s-unfold"
      circle
      type="primary"
      @click="forceShowLeftSidebar = true"
      title="Show conversation list"
    />

    <!-- Part A: Left Sidebar - Conversation List -->
    <div class="conversations-sidebar" :class="{ 'force-show': forceShowLeftSidebar }" :style="{ width: sidebarWidth + 'px' }">
      <div class="resize-handle" @mousedown="startResize"></div>
      <!-- Close button when force-shown -->
      <el-button
        v-if="forceShowLeftSidebar"
        class="panel-close-btn"
        icon="el-icon-close"
        circle
        size="small"
        @click="closeForcedPanels"
        title="Hide sidebar"
      />

      <div class="conversations-header">
        <h2>Conversations</h2>
        <el-button
          type="text"
          icon="el-icon-refresh"
          @click="loadDialogues"
          :loading="isLoadingDialogues"
        />
      </div>

      <div class="dialogue-list">
        <div v-if="isLoadingDialogues" class="loading-container">
          <i class="el-icon-loading"></i>
          <span>Loading conversations...</span>
        </div>

        <div v-else-if="dialogues.length === 0" class="empty-state">
          <i class="el-icon-chat-line-round" style="font-size: 48px; color: #909399;"></i>
          <p>No conversations yet</p>
        </div>

        <div
          v-else
          v-for="dialogue in dialogues"
          :key="dialogue.userId"
          :class="['dialogue-item', {
            'active': selectedDialogueUserId === dialogue.userId,
            'has-unread': dialogue.unreadCount > 0
          }]"
          @click="selectDialogue(dialogue)"
        >
          <el-avatar
            :size="40"
            :src="dialogue.userAvatar"
            :style="{
              border: getBorderStyleByUserType(dialogue.userType),
              backgroundColor: isValidAvatar(dialogue.userAvatar) ? 'white' : '#e0e0e0',
              color: '#409EFF',
              flexShrink: 0
            }"
          >
            {{ isValidAvatar(dialogue.userAvatar) ? '' : getInitial(dialogue.userName) }}
          </el-avatar>
          <div class="dialogue-info">
            <div class="dialogue-header">
              <span class="dialogue-name">
                {{ dialogue.userName || 'Unknown' }}
                <span v-if="dialogue.userId === $store.state.user_id"> (You)</span>
              </span>
              <div class="dialogue-meta">
                <span class="dialogue-time">{{ formatMessageTime(dialogue.lastMessageSendTime) }}</span>
                <el-badge v-if="dialogue.unreadCount > 0" is-dot class="unread-indicator" />
              </div>
            </div>
            <div class="dialogue-preview">{{ dialogue.lastMessagePreview }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Part B: Conversation Message List (in middle with flex:1) -->
    <div v-if="selectedDialogue" class="content-panel middle-panel">
      <div class="conversation-view">
        <!-- Conversation header -->
        <div class="conversation-header">
          <h3>
            <span class="conversation-with-text">Conversation with</span>
            <div
              @click="openUserProfile(selectedDialogue.userId)"
              class="user-profile-link"
            >
              <el-avatar
                :size="24"
                :src="selectedDialogue.userAvatar"
                :style="{
                  border: getBorderStyleByUserType(selectedDialogue.userType),
                  backgroundColor: isValidAvatar(selectedDialogue.userAvatar) ? 'white' : '#e0e0e0',
                  color: '#409EFF',
                  marginLeft: '8px',
                  marginRight: '6px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '12px',
                  fontWeight: '600'
                }"
              >
                {{ isValidAvatar(selectedDialogue.userAvatar) ? '' : getInitial(selectedDialogue.userName) }}
              </el-avatar>
              <span class="user-name-link">{{ selectedDialogue.userName }}</span>
            </div>
            <span v-if="selectedDialogue.userId === $store.state.user_id"> (You)</span>
          </h3>
        </div>

        <!-- Conversation messages -->
        <div
          class="conversation-messages"
          ref="dialogueMessages"
          @scroll="handleConversationScroll"
        >
          <!-- Load more indicator -->
          <div v-if="isLoadingMoreConversationMessages" class="loading-more-indicator">
            <i class="el-icon-loading"></i>
            <span>Loading more messages...</span>
          </div>

          <!-- Messages -->
          <div
            v-for="message in conversationMessages"
            :key="message._id?.$oid || message._id?.$id || message.id"
            :class="[
              'conversation-message-wrapper',
              { 'sent-by-me': isSentByMe(message) }
            ]"
          >
            <!-- Avatar for other user (left side) -->
            <el-avatar
              v-if="!isSentByMe(message)"
              :size="36"
              :src="selectedDialogue.userAvatar"
              :style="{
                border: getBorderStyleByUserType(selectedDialogue.userType),
                backgroundColor: isValidAvatar(selectedDialogue.userAvatar) ? 'white' : '#e0e0e0',
                color: '#409EFF',
                flexShrink: 0
              }"
              class="message-avatar"
            >
              {{ isValidAvatar(selectedDialogue.userAvatar) ? '' : getInitial(selectedDialogue.userName) }}
            </el-avatar>

            <div
              :class="[
                'conversation-message',
                { 'selected': selectedDialogueMessage && (selectedDialogueMessage._id?.$oid || selectedDialogueMessage._id?.$id || selectedDialogueMessage.id) === (message._id?.$oid || message._id?.$id || message.id) }
              ]"
              @click="selectConversationMessage(message)"
            >
              <div class="conv-msg-content">{{ getMessagePreview(message) }}</div>
              <!-- Show CardPreview for card notifications -->
              <CardPreview
                v-if="message.message_content_type === 'card_share_notification' || message.message_content_type === 'card_remind_notification' || message.message_type === 'card_remind_notification'"
                :cardTitle="message.message_content_params?.card_info?.title || message.title || 'Card'"
                :collectionTitle="message.message_content_params?.card_info?.collection_title || ''"
                :groupTitle="message.message_content_params?.card_info?.group_title || ''"
                :groupIndex="message.message_content_params?.card_info?.group_index"
                style="margin: 0;"
              />
              <!-- Show PostPreview for generative content notifications -->
              <PostPreview
                v-if="message.message_content_type === 'generative_content_notification' && message.message_content_params?.content_info"
                :postTitle="message.message_content_params.content_info.title"
                :collectionTitle="message.message_content_params.content_info.collection_title"
                :groupTitle="message.message_content_params.content_info.group_title"
                :groupIndex="message.message_content_params.content_info.group_index"
                :quizCount="message.message_content_params.content_info.quiz_count"
                :postData="message.message_content_params.content_info"
                style="margin: 0;"
              />
              <div class="conv-msg-time">{{ formatMessageTime(message.send_time) }}</div>
            </div>

            <!-- Avatar for logged-in user (right side) -->
            <el-avatar
              v-if="isSentByMe(message)"
              :size="36"
              :src="loggedInUserAvatar"
              :style="{
                border: getBorderStyleByUserType(loggedInUserType),
                backgroundColor: isValidAvatar(loggedInUserAvatar) ? 'white' : '#e0e0e0',
                color: '#409EFF',
                flexShrink: 0
              }"
              class="message-avatar"
            >
              {{ isValidAvatar(loggedInUserAvatar) ? '' : getInitial(loggedInUserName) }}
            </el-avatar>
          </div>
        </div>

        <!-- Reply input -->
        <div class="conversation-reply">
          <el-input
            type="textarea"
            :rows="3"
            placeholder="Type a message..."
            v-model="replyMessage"
            @keydown.enter.ctrl="sendReply"
          />
          <el-button
            type="primary"
            @click="sendReply"
            :disabled="!replyMessage.trim()"
          >
            Send
          </el-button>
        </div>
      </div>
    </div>

    <!-- Part C: Message Detail (on right with fixed width) -->
    <div
      v-if="shouldShowRightPanel"
      :class="['content-panel', 'message-content-panel', 'right-panel', { 'collapsed': !showRightPanel }]"
    >
      <div v-if="showRightPanel" class="resize-handle-right" @mousedown.prevent="startRightPanelResize"></div>
      <!-- Close button when force-shown -->
      <el-button
        v-if="forceShowRightPanel && showRightPanel"
        class="panel-close-btn"
        icon="el-icon-close"
        circle
        size="small"
        @click="closeForcedPanels"
        title="Hide message details"
      />
      <!-- Expand button when collapsed -->
      <div v-if="!showRightPanel" class="expand-btn" @click="showRightPanel = true">
        <i class="el-icon-d-arrow-left"></i>
      </div>
      <div class="panel-content" v-if="showRightPanel">
        <div class="message-detail" :key="selectedDialogueMessage._id?.$oid || selectedDialogueMessage._id?.$id || selectedDialogueMessage.id">
          <!-- Message header -->
          <div class="message-detail-header">
            <div class="sender-info">
              <el-avatar
                :size="40"
                :src="getMessageSender(selectedDialogueMessage).avatar"
                :style="{
                  border: getBorderStyleByUserType(getMessageSender(selectedDialogueMessage).userType),
                  backgroundColor: isValidAvatar(getMessageSender(selectedDialogueMessage).avatar) ? 'white' : '#e0e0e0',
                  color: '#409EFF',
                  marginRight: '12px',
                  flexShrink: 0
                }"
              >
                {{ isValidAvatar(getMessageSender(selectedDialogueMessage).avatar) ? '' : getInitial(getMessageSender(selectedDialogueMessage).name) }}
              </el-avatar>
              <div>
                <h3>{{ getMessageSender(selectedDialogueMessage).name }}</h3>
                <span class="message-timestamp">{{ formatFullTime(selectedDialogueMessage.send_time) }}</span>
              </div>
            </div>
            <el-button
              type="text"
              icon="el-icon-close"
              @click="showRightPanel = false"
              class="collapse-btn-detail"
            />
          </div>

          <!-- Message content -->
          <div class="message-detail-content">
            <!-- Card share notification (viewer is not creator) -->
            <div v-if="selectedDialogueMessage.message_content_type === 'card_share_notification'" class="card-share-content">
              <DirectReadCard
                v-if="selectedDialogueMessageCardData"
                :card="selectedDialogueMessageCardData"
                :collections="[]"
                :showAnswer="true"
                :showReminder="false"
              />
              <div v-else class="loading-card">
                <i class="el-icon-loading"></i>
                <span>Loading card...</span>
              </div>
            </div>

            <!-- Card remind notification (viewer is creator) -->
            <div v-else-if="selectedDialogueMessage.message_content_type === 'card_remind_notification' || selectedDialogueMessage.message_type === 'card_remind_notification'" class="card-share-content">
              <DirectReadCard
                v-if="selectedDialogueMessageCardData"
                :card="selectedDialogueMessageCardData"
                :collections="[]"
                :showAnswer="true"
                :showReminder="true"
              />
              <div v-else class="loading-card">
                <i class="el-icon-loading"></i>
                <span>Loading card...</span>
              </div>
            </div>

            <!-- Generative content notification (post) -->
            <div v-else-if="selectedDialogueMessage.message_content_type === 'generative_content_notification'" class="post-share-content">
              <div v-if="selectedDialogueMessage.content" class="message-content-text">
                {{ selectedDialogueMessage.content }}
              </div>
              <PostPreview
                v-if="selectedDialogueMessagePostData"
                :postTitle="selectedDialogueMessagePostData.title"
                :quizCount="selectedDialogueMessagePostData.quiz_count"
                :groupIndex="selectedDialogueMessagePostData.group_index"
                :collectionTitle="selectedDialogueMessagePostData.collection_title"
                :groupTitle="selectedDialogueMessagePostData.group_title"
                :postData="selectedDialogueMessagePostData"
              />
              <DirectReadPost
                v-if="selectedDialogueMessagePostData"
                :post="selectedDialogueMessagePostData"
              />
              <div v-else class="loading-post">
                <i class="el-icon-loading"></i>
                <span>Loading post...</span>
              </div>
            </div>

            <!-- Regular text message -->
            <div v-else class="message-content-text">
              {{ selectedDialogueMessage.content }}
            </div>
          </div>

          <!-- Message navigation buttons -->
          <div class="message-navigation">
            <el-button
              v-if="canNavigateToPreviousConversationMessage"
              class="nav-btn-up"
              icon="el-icon-arrow-up"
              circle
              size="small"
              @click="navigateToPreviousConversationMessage"
              title="Previous message"
            />
            <el-button
              v-if="canNavigateToNextConversationMessage"
              class="nav-btn-down"
              icon="el-icon-arrow-down"
              circle
              size="small"
              @click="navigateToNextConversationMessage"
              title="Next message"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Empty state when nothing selected -->
    <div v-if="!selectedDialogue" class="empty-content">
      <i class="el-icon-chat-line-round" style="font-size: 64px; color: #c0c4cc;"></i>
      <p style="color: #909399; margin-top: 20px;">Select a conversation to view</p>
    </div>
  </div>
</template>

<script>
import { postRequest } from '@/apis/front'
import { getUserId, getUserName } from '@/utils/store'
import DirectReadCard from '@/components/DirectReadCard.vue'
import CardPreview from '@/components/CardPreview.vue'
import PostPreview from '@/components/PostPreview.vue'
import DirectReadPost from '@/components/DirectReadPost.vue'

export default {
  name: 'Conversations',
  components: {
    DirectReadCard,
    CardPreview,
    PostPreview,
    DirectReadPost
  },
  data() {
    return {
      // Dialogue data
      dialogues: [],
      selectedDialogue: null,
      selectedDialogueUserId: null,
      selectedDialogueMessage: null,

      // Layout state
      showRightPanel: true,

      // Conversation messages
      conversationMessages: [],
      replyMessage: '',

      // Pagination for conversation messages
      conversationMessagesLoadTime: null,
      conversationMessagesLimit: 10,
      hasMoreConversationMessages: true,
      isLoadingMoreConversationMessages: false,

      // Loading states
      isLoadingDialogues: false,

      // Sidebar resize
      sidebarWidth: 350,
      isResizing: false,
      resizeStartX: 0,
      resizeStartWidth: 0,

      // Right panel resize
      rightPanelWidth: 400,
      isResizingRightPanel: false,
      rightPanelResizeStartX: 0,
      rightPanelResizeStartWidth: 0,

      // Card data for card share notifications
      selectedDialogueMessageCardData: null,

      // Post data for generative content notifications
      selectedDialogueMessagePostData: null,

      // Force show panels on mobile
      forceShowLeftSidebar: false,
      forceShowRightPanel: false,

      // Track window width for responsive behavior
      windowWidth: window.innerWidth
    }
  },
  computed: {
    isRightPanelHiddenByResponsive() {
      return this.windowWidth < 1000
    },
    isLeftSidebarHiddenByResponsive() {
      return this.windowWidth < 780
    },
    currentConversationMessageIndex() {
      if (!this.selectedDialogueMessage) return -1
      const messageId = this.selectedDialogueMessage._id?.$oid || this.selectedDialogueMessage._id?.$id || this.selectedDialogueMessage.id
      return this.conversationMessages.findIndex(m => (m._id?.$oid || m._id?.$id || m.id) === messageId)
    },
    canNavigateToPreviousConversationMessage() {
      return this.currentConversationMessageIndex >= 0
    },
    canNavigateToNextConversationMessage() {
      return this.currentConversationMessageIndex >= 0
    },
    shouldShowRightPanel() {
      if (!this.selectedDialogueMessage) return false
      const msg = this.selectedDialogueMessage
      return msg.message_content_type === 'card_share_notification' ||
             msg.message_content_type === 'card_remind_notification' ||
             msg.message_type === 'card_remind_notification' ||
             msg.message_content_type === 'generative_content_notification'
    },
    loggedInUserName() {
      return getUserName() || this.$store.state.user_nickName || 'User'
    },
    loggedInUserAvatar() {
      return this.$store.state.user_avatar_address_small || ''
    },
    loggedInUserType() {
      return this.$store.state.user_type || 'individual'
    }
  },
  watch: {
    async selectedDialogueMessage(newMessage) {
      console.log('selectedDialogueMessage watcher triggered with:', newMessage)

      this.selectedDialogueMessageCardData = null
      this.selectedDialogueMessagePostData = null

      const isCardNotification = newMessage && (
        newMessage.message_content_type === 'card_share_notification' ||
        newMessage.message_content_type === 'card_remind_notification' ||
        newMessage.message_type === 'card_remind_notification'
      )
      console.log('isCardNotification:', isCardNotification)

      if (isCardNotification) {
        console.log('Calling loadCardData')
        await this.loadCardData(newMessage)
      }

      const isPostNotification = newMessage && newMessage.message_content_type === 'generative_content_notification'
      console.log('isPostNotification:', isPostNotification)

      if (isPostNotification) {
        console.log('Calling loadPostData')
        await this.loadPostData(newMessage)
      }
    }
  },
  mounted() {
    this.loadDialogues()
    // Add global mouse event listeners for resize
    document.addEventListener('mousemove', this.handleResize)
    document.addEventListener('mouseup', this.stopResize)
    document.addEventListener('mousemove', this.handleRightPanelResize)
    document.addEventListener('mouseup', this.stopRightPanelResize)
    // Add window resize listener for responsive behavior
    window.addEventListener('resize', this.handleWindowResize)
  },
  beforeDestroy() {
    // Remove global mouse event listeners
    document.removeEventListener('mousemove', this.handleResize)
    document.removeEventListener('mouseup', this.stopResize)
    document.removeEventListener('mousemove', this.handleRightPanelResize)
    document.removeEventListener('mouseup', this.stopRightPanelResize)
    // Remove window resize listener
    window.removeEventListener('resize', this.handleWindowResize)
  },
  methods: {
    // Sidebar resize methods
    startResize(e) {
      this.isResizing = true
      this.resizeStartX = e.clientX
      this.resizeStartWidth = this.sidebarWidth
      e.preventDefault()
    },
    handleResize(e) {
      if (!this.isResizing) return

      const deltaX = e.clientX - this.resizeStartX
      const newWidth = this.resizeStartWidth + deltaX

      if (newWidth >= 250 && newWidth <= 600) {
        this.sidebarWidth = newWidth
      }
    },
    stopResize() {
      this.isResizing = false
    },

    // Right panel resize methods
    startRightPanelResize(e) {
      this.isResizingRightPanel = true
      this.rightPanelResizeStartX = e.clientX
      this.rightPanelResizeStartWidth = this.rightPanelWidth
      e.preventDefault()
    },
    handleRightPanelResize(e) {
      if (!this.isResizingRightPanel) return

      const deltaX = this.rightPanelResizeStartX - e.clientX
      const newWidth = this.rightPanelResizeStartWidth + deltaX

      if (newWidth >= 300 && newWidth <= 800) {
        this.rightPanelWidth = newWidth
      }
    },
    stopRightPanelResize() {
      this.isResizingRightPanel = false
    },

    // Determine if message should be aligned right (sent by me)
    isSentByMe(message) {
      const currentUserId = this.$store.state.user_id

      // Check if this is a self-conversation (talking to yourself)
      const isSelfConversation = this.selectedDialogue && this.selectedDialogue.userId === currentUserId

      if (isSelfConversation) {
        // For self-conversation:
        // - If message has message_content_type field set → align left (from "other side")
        // - If message does NOT have message_content_type field → align right (from user)
        if (message.message_content_type && message.message_content_type !== '') {
          return false // Align left (from "other side")
        } else {
          return true // Align right (normal message from user)
        }
      } else {
        // Normal conversation: align right if sender is current user
        return (message.sender?._id?.$oid || message.sender?._id?.$id || message.sender_id) === currentUserId
      }
    },

    async loadDialogues() {
      this.isLoadingDialogues = true
      try {
        const response = await postRequest('getConversationList', {
          user_id: getUserId()
        })

        if (response.data.code === 0) {
          const conversations = response.data.data || []

          this.dialogues = conversations.map(conv => {
            let messagePreview = ''
            if (conv.latest_message?.content) {
              messagePreview = conv.latest_message.content
            } else if (conv.latest_message_preview) {
              messagePreview = conv.latest_message_preview
            } else if (conv.last_message_preview) {
              messagePreview = conv.last_message_preview
            }

            return {
              conversationId: conv._id?.$oid || conv._id?.$id || conv.conversation_id,
              userId: conv.other_user?._id?.$oid || conv.other_user?._id?.$id || conv.other_user_id,
              userName: conv.other_user?.user_nickName || conv.other_user_name || 'Unknown',
              userAvatar: conv.other_user?.user_avatar_address_small || conv.other_user_avatar,
              userType: conv.other_user?.user_type || conv.other_user_type,
              unreadCount: conv.unread_count || 0,
              lastMessageSendTime: conv.latest_message?.send_time,
              lastMessagePreview: messagePreview
            }
          })
        }
      } catch (error) {
        console.error('Failed to load dialogues:', error)
        this.$message.error('Failed to load conversations')
      } finally {
        this.isLoadingDialogues = false
      }
    },

    async selectDialogue(dialogue) {
      this.selectedDialogue = dialogue
      this.selectedDialogueUserId = dialogue.userId
      this.selectedDialogueMessage = null

      // Load conversation messages
      await this.loadConversationMessagesByConversationId(dialogue.conversationId)
    },

    selectConversationMessage(message) {
      console.log('selectConversationMessage called with:', message)
      console.log('Message content type:', message.message_content_type)
      console.log('Message type:', message.message_type)

      // Only select and show right panel if message has card or post content
      const isCardOrPost = message.message_content_type === 'card_share_notification' ||
                          message.message_content_type === 'card_remind_notification' ||
                          message.message_type === 'card_remind_notification' ||
                          message.message_content_type === 'generative_content_notification'

      console.log('isCardOrPost:', isCardOrPost)

      if (isCardOrPost) {
        console.log('Setting selectedDialogueMessage and showing right panel')
        this.selectedDialogueMessage = message
        this.showRightPanel = true
      }

      if (!message.receiver_read_status) {
        const messageId = message._id?.$oid || message._id?.$id || message.id
        this.markAsRead(messageId)
        message.receiver_read_status = true
      }
    },

    async loadConversationMessagesByConversationId(conversationId, reset = true) {
      if (!conversationId) return

      try {
        if (reset) {
          this.conversationMessagesLoadTime = null
          this.hasMoreConversationMessages = true
        }

        const params = {
          user_id: getUserId(),
          conversation_id: conversationId,
          limit: this.conversationMessagesLimit
        }

        if (this.conversationMessagesLoadTime) {
          params.load_time = this.conversationMessagesLoadTime
        }

        const response = await postRequest('getConversationMessages', params)

        if (response.data.code === 0) {
          const newMessages = response.data.data || []

          if (reset) {
            this.conversationMessages = newMessages
          } else {
            this.conversationMessages = [...newMessages, ...this.conversationMessages]
          }

          if (newMessages.length > 0) {
            const sortedByTime = [...newMessages].sort((a, b) => (a.send_time || 0) - (b.send_time || 0))
            this.conversationMessagesLoadTime = sortedByTime[0].send_time
          }

          if (newMessages.length < this.conversationMessagesLimit) {
            this.hasMoreConversationMessages = false
          }

          this.conversationMessages.sort((a, b) => {
            const timeA = a.send_time || 0
            const timeB = b.send_time || 0
            return timeA - timeB
          })
        } else {
          if (reset) {
            this.conversationMessages = []
          }
        }
      } catch (error) {
        console.error('Failed to load conversation messages:', error)
        if (reset) {
          this.conversationMessages = []
        }
      }

      if (reset) {
        this.$nextTick(() => {
          const container = this.$refs.dialogueMessages
          if (container) {
            container.scrollTop = container.scrollHeight
          }
        })
      }
    },

    async loadMoreConversationMessages() {
      if (this.isLoadingMoreConversationMessages || !this.hasMoreConversationMessages) return

      const conversationId = this.selectedDialogue?.conversationId
      if (!conversationId) return

      const container = this.$refs.dialogueMessages
      const oldScrollHeight = container ? container.scrollHeight : 0

      this.isLoadingMoreConversationMessages = true
      try {
        await this.loadConversationMessagesByConversationId(conversationId, false)

        this.$nextTick(() => {
          if (container) {
            const newScrollHeight = container.scrollHeight
            container.scrollTop = newScrollHeight - oldScrollHeight
          }
        })
      } catch (error) {
        console.error('Failed to load more conversation messages:', error)
      } finally {
        this.isLoadingMoreConversationMessages = false
      }
    },

    handleConversationScroll(event) {
      const container = event.target
      if (container.scrollTop < 50 && !this.isLoadingMoreConversationMessages && this.hasMoreConversationMessages) {
        this.loadMoreConversationMessages()
      }
    },

    async sendReply() {
      if (!this.replyMessage.trim()) return

      const conversationId = this.selectedDialogue.conversationId
      const targetUserId = this.selectedDialogue.userId

      if (!targetUserId || !conversationId) {
        this.$message.error('Cannot send message: missing conversation information')
        return
      }

      try {
        const messageData = {
          role: 'user',
          content: this.replyMessage,
          sender_id: getUserId(),
          send_type: 'REPLY',
          receiver_id: targetUserId,
          conversation_id: conversationId
        }

        const response = await postRequest('createConversationMessage', {
          conversation_id: conversationId,
          message: messageData,
          send_time: Math.floor(Date.now() / 1000)
        })

        if (response.data.code === 0) {
          this.replyMessage = ''
          await this.loadConversationMessagesByConversationId(conversationId)
          await this.loadDialogues()
          this.$message.success('Message sent')
        } else {
          this.$message.error(response.data.msg || 'Failed to send message')
        }
      } catch (error) {
        this.$message.error('Failed to send message')
      }
    },

    async markAsRead(messageId) {
      try {
        await postRequest('markMessageAsRead', {
          user_id: getUserId(),
          message_id: messageId
        })
      } catch (error) {
        console.error('Failed to mark message as read:', error)
      }
    },

    async navigateToPreviousConversationMessage() {
      if (this.currentConversationMessageIndex > 0) {
        const prevMessage = this.conversationMessages[this.currentConversationMessageIndex - 1]
        this.selectConversationMessage(prevMessage)
      } else if (this.currentConversationMessageIndex === 0) {
        if (this.hasMoreConversationMessages) {
          const oldScrollHeight = this.$refs.dialogueMessages?.scrollHeight || 0
          await this.loadMoreConversationMessages()
          if (this.conversationMessages.length > 0) {
            this.selectConversationMessage(this.conversationMessages[0])
            this.$nextTick(() => {
              const container = this.$refs.dialogueMessages
              if (container) {
                const newScrollHeight = container.scrollHeight
                container.scrollTop = newScrollHeight - oldScrollHeight
              }
            })
          }
        } else {
          this.$message.info('No more messages')
        }
      }
    },

    async navigateToNextConversationMessage() {
      if (this.currentConversationMessageIndex < this.conversationMessages.length - 1) {
        const nextMessage = this.conversationMessages[this.currentConversationMessageIndex + 1]
        this.selectConversationMessage(nextMessage)
      } else if (this.currentConversationMessageIndex === this.conversationMessages.length - 1) {
        this.$message.info('This is the latest message')
      }
    },

    closeForcedPanels() {
      this.forceShowLeftSidebar = false
      if (this.forceShowRightPanel) {
        this.showRightPanel = false
      }
      this.forceShowRightPanel = false
    },

    handleWindowResize() {
      this.windowWidth = window.innerWidth
    },

    getMessagePreview(message) {
      if (message.message_type === 'post') {
        return `Shared a post: ${message.post?.title || 'Untitled'}`
      }
      const text = this.stripHtmlTags(message.content || '')
      return text.length > 300 ? text.substring(0, 300) + '...' : text
    },

    stripHtmlTags(html) {
      const doc = new DOMParser().parseFromString(html || '', 'text/html')
      return doc.body.textContent || ''
    },

    formatMessageTime(timestamp) {
      if (!timestamp) return ''
      const date = new Date(timestamp * 1000)
      const now = new Date()
      const diff = now - date

      if (diff < 86400000) {
        return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
      } else {
        // Check if the year is different from current year
        if (date.getFullYear() !== now.getFullYear()) {
          return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
        } else {
          return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
        }
      }
    },

    formatFullTime(timestamp) {
      if (!timestamp) return ''
      return new Date(timestamp * 1000).toLocaleString('en-US')
    },

    getBorderStyleByUserType(userType) {
      const type = userType || 'individual'
      switch(type) {
        case 'individual':
          return '1px solid #1890ff'
        case 'institution':
          return '1px solid #67C23A'
        case 'agent':
          return '1px solid #E6A23C'
        default:
          return '1px solid #e0e0e0'
      }
    },

    isValidAvatar(avatar) {
      return avatar && avatar !== '' && avatar !== 'undefined'
    },

    getInitial(name) {
      return name ? name.charAt(0).toUpperCase() : '?'
    },

    openUserProfile(userId) {
      // Construct the URL with hash routing
      const baseUrl = `${window.location.protocol}//${window.location.host}`
      window.open(`${baseUrl}/#/profile?id=${userId}`, '_blank')
    },

    getMessageSender(message) {
      if (message.sender?.user_nickName) {
        return {
          name: message.sender.user_nickName,
          avatar: message.sender.user_avatar_address_small,
          userType: message.sender.user_type
        }
      }

      if (this.selectedDialogue) {
        return {
          name: this.selectedDialogue.userName || 'Unknown',
          avatar: this.selectedDialogue.userAvatar,
          userType: this.selectedDialogue.userType
        }
      }

      return {
        name: 'Unknown',
        avatar: null,
        userType: null
      }
    },

    getCardTitle(message) {
      if (message.message_content_params?.card_info?.title) {
        return message.message_content_params.card_info.title
      }
      if (message.title) {
        const match = message.title.match(/:\s*(.+)$/)
        if (match) {
          return match[1]
        }
      }
      return 'Knowit Card'
    },

    getCardTags(message) {
      if (message.message_content_params?.card_info?.labels) {
        return message.message_content_params.card_info.labels
      }
      return []
    },

    async loadCardData(message) {
      console.log('loadCardData called with message:', message)
      console.log('message.message_content_params:', message.message_content_params)

      if (message.message_content_params?.card_info) {
        console.log('Using card_info from message_content_params')
        const cardInfo = message.message_content_params.card_info
        const cardData = {
          id: cardInfo._id?.$oid || cardInfo._id?.$id || cardInfo.id,
          ...cardInfo
        }
        this.selectedDialogueMessageCardData = cardData
        console.log('Set selectedDialogueMessageCardData to:', cardData)
        return
      }

      if (message.card_data) {
        console.log('Using card_data from message')
        this.selectedDialogueMessageCardData = message.card_data
        console.log('Set selectedDialogueMessageCardData to:', message.card_data)
        return
      }

      const cardId = message.message_content_params?.card_id || message.card_id || message.content_id
      console.log('cardId:', cardId)
      if (!cardId) {
        console.error('No card_id found in message')
        return
      }

      try {
        console.log('Fetching card data from API')
        const response = await postRequest('getCard', {
          user_id: getUserId(),
          card_id: cardId
        })

        console.log('API response:', response)

        if (response.data.code === 0 && response.data.data) {
          this.selectedDialogueMessageCardData = response.data.data
          console.log('Set selectedDialogueMessageCardData to:', response.data.data)
        } else {
          this.$message.error('Failed to load card')
        }
      } catch (error) {
        console.error('Error loading card data:', error)
        this.$message.error('Failed to load card')
      }
    },

    async loadPostData(message) {
      console.log('loadPostData called with message:', message)
      console.log('message.message_content_params:', message.message_content_params)

      if (message.message_content_params?.post_info) {
        console.log('Using post_info from message_content_params')
        this.selectedDialogueMessagePostData = message.message_content_params.post_info
        console.log('Set selectedDialogueMessagePostData to:', message.message_content_params.post_info)
        return
      }

      const postId = message.message_content_params?.content_id ||
                     message.message_content_params?.post_id ||
                     message.post_id ||
                     message.content_id
      console.log('postId:', postId)
      if (!postId) {
        console.error('No post_id found in message')
        return
      }

      try {
        console.log('Fetching post data from API')
        const response = await postRequest('getPost', {
          user_id: getUserId(),
          post_id: postId
        })
        console.log('API response:', response)

        if (response.data.code === 0 && response.data.data) {
          this.selectedDialogueMessagePostData = response.data.data
          console.log('Successfully set selectedDialogueMessagePostData:', this.selectedDialogueMessagePostData)
        } else {
          console.error('Failed to load post - response code:', response.data.code)
          this.$message.error('Failed to load post')
        }
      } catch (error) {
        console.error('Error loading post data:', error)
        this.$message.error('Failed to load post')
      }
    }
  }
}
</script>

<style scoped>
.conversations-container {
  display: flex;
  height: 100vh;
  width: 100vw;
  max-width: 100vw;
  background-color: #ebebeb;
  overflow: hidden;
  box-sizing: border-box;
}

.conversations-container :deep(.el-avatar) {
  flex-shrink: 0;
  min-width: 40px;
  min-height: 40px;
}

.conversations-container :deep(.el-avatar > img) {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.conversations-container.is-resizing {
  cursor: col-resize;
  user-select: none;
}

.conversations-container.is-resizing * {
  cursor: col-resize !important;
  user-select: none;
}

.conversations-container.is-resizing-right * {
  cursor: col-resize !important;
  user-select: none;
}

/* Left Sidebar */
.conversations-sidebar {
  min-width: 250px;
  max-width: 600px;
  background: #f5f5f5;
  border-right: 1px solid #d0d0d0;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  overflow: hidden;
  position: relative;
}

.resize-handle {
  position: absolute;
  right: 0;
  top: 0;
  bottom: 0;
  width: 5px;
  cursor: col-resize;
  z-index: 100;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
}

.resize-handle::before {
  content: '';
  position: absolute;
  top: 0;
  bottom: 0;
  left: 2px;
  width: 1px;
  background: #e4e7ed;
}

.resize-handle::after {
  content: '';
  width: 4px;
  height: 32px;
  background: #d0d0d0;
  border-radius: 2px;
  position: relative;
  z-index: 1;
  transition: background-color 0.2s;
}

.resize-handle:hover::after {
  background-color: #409eff;
}

.resize-handle-right {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 5px;
  cursor: col-resize;
  z-index: 1000;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
}

.resize-handle-right::before {
  content: '';
  position: absolute;
  top: 0;
  bottom: 0;
  left: 2px;
  width: 1px;
  background: #e4e7ed;
}

.resize-handle-right::after {
  content: '';
  width: 4px;
  height: 32px;
  background: #d0d0d0;
  border-radius: 2px;
  position: relative;
  z-index: 1;
  transition: background-color 0.2s;
}

.resize-handle-right:hover::after {
  background-color: #409eff;
}

.conversations-header {
  padding: 12px 16px;
  border-bottom: 1px solid #d0d0d0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;
  background: #ebebeb;
}

.conversations-header h2 {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
}

.dialogue-list {
  flex: 1;
  overflow-y: auto;
}

.loading-container,
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  color: #909399;
}

.dialogue-item {
  padding: 15px 20px;
  border-bottom: 1px solid #e0e0e0;
  cursor: pointer;
  transition: background-color 0.3s;
  display: flex;
  align-items: center;
  gap: 12px;
}

.dialogue-item:hover {
  background-color: #ebebeb;
}

.dialogue-item.active {
  background-color: #d9ecff;
  border-left: 3px solid #409eff;
}

.dialogue-item.has-unread {
  font-weight: 500;
}

.dialogue-info {
  flex: 1;
  min-width: 0;
}

.dialogue-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
  gap: 8px;
}

.dialogue-name {
  font-weight: 600;
  color: #303133;
  font-size: 14px;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.dialogue-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.dialogue-preview {
  color: #909399;
  font-size: 13px;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  line-height: 1.4;
}

.dialogue-time {
  font-size: 12px;
  color: #c0c4cc;
}

/* Content Panels */
.content-panel {
  background: white;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.middle-panel {
  min-width: 500px;
  flex: 1;
  background: #ffffff;
  box-shadow:
    -4px 0 12px rgba(0, 0, 0, 0.08),
    4px 0 12px rgba(0, 0, 0, 0.08);
  z-index: 10;
  position: relative;
  border-left: 1px solid #c0c0c0;
  border-right: 1px solid #c0c0c0;
}

.right-panel-visible .middle-panel {
  flex: 0 0 500px;
}

.right-panel {
  width: 500px;
  min-width: 800px;
  position: relative;
  border-left: 1px solid #d0d0d0;
  overflow: hidden;
  background: #f5f5f5;
}

.right-panel.collapsed {
  width: 50px !important;
  min-width: 50px !important;
}

.secondary-panel .message-detail-header,
.secondary-panel .conversation-header {
  background: #ebebeb;
}

.secondary-panel .conversation-messages {
  background-image: url('~@/assets/conversation_bgd_gradient.jpeg');
  background-size: 100% auto;
  background-position: top center;
  background-repeat: repeat-y;
}

.secondary-panel .message-detail-content {
  background: #f0f0f0;
}

.expand-btn {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #909399;
  font-size: 18px;
  transition: all 0.3s;
}

.expand-btn:hover {
  background: #f5f7fa;
  color: #409eff;
}

.panel-content {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.right-panel .panel-content {
  margin-left: 5px;
  width: calc(100% - 5px);
  background: white;
  height: 100%;
}

.message-detail {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
}

.message-navigation {
  position: absolute;
  bottom: 20px;
  right: 20px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: flex-end;
  z-index: 100;
}

.message-navigation .el-button--small {
  width: 36px !important;
  height: 36px !important;
  min-width: 36px !important;
  padding: 9px !important;
  border-radius: 4px !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  background: #ecf5ff !important;
  border-color: #b3d8ff !important;
  color: #409eff !important;
}

.message-navigation .el-button--small:hover {
  background: #409eff !important;
  border-color: #409eff !important;
  color: white !important;
}

.message-detail-header {
  padding: 12px 16px;
  border-bottom: 1px solid #e4e7ed;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;
  background: #fafafa;
}

.sender-info {
  display: flex;
  align-items: center;
}

.sender-info h3 {
  margin: 0 0 4px 0;
  font-size: 16px;
}

.message-timestamp {
  font-size: 13px;
  color: #909399;
}

.message-detail-content {
  flex: 1;
  padding: 20px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
}

.message-content-text {
  font-size: 15px;
  line-height: 1.8;
  color: #303133;
  white-space: pre-wrap;
  word-wrap: break-word;
  max-width: 600px;
  width: 100%;
}

.card-share-content {
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: flex-start;
}

.post-share-content {
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 16px;
}

.post-share-content .message-content-text {
  width: 100%;
  max-width: 700px;
  text-align: left;
}

.post-share-content :deep(.post-preview) {
  width: 100%;
  max-width: 700px;
}

.post-share-content :deep(.direct-read-post) {
  width: 100%;
}

.loading-card,
.loading-post {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  color: #909399;
  gap: 12px;
}

.loading-card i,
.loading-post i {
  font-size: 32px;
}

/* Conversation View (Middle Panel) */
.conversation-view {
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  overflow: hidden;
}

/* Conversation Panel */
.conversation-header {
  padding: 12px 16px;
  border-bottom: 1px solid #e4e7ed;
  flex-shrink: 0;
  background: #fafafa;
}

.conversation-header h3 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  padding-right: 40px;
  display: flex;
  align-items: center;
}

.conversation-with-text {
  font-weight: 400;
  color: #909399;
}

.user-profile-link {
  display: flex;
  align-items: center;
  text-decoration: none;
  color: inherit;
  transition: opacity 0.2s;
  cursor: pointer;
}

.user-profile-link:hover {
  opacity: 0.7;
}

.user-name-link {
  font-weight: 600;
  color: #303133;
}

.collapse-btn {
  position: absolute;
  top: 12px;
  right: 16px;
  font-size: 18px;
  color: #909399;
  padding: 4px;
}

.collapse-btn:hover {
  color: #409eff;
}

.collapse-btn-detail {
  font-size: 18px;
  color: #909399;
  padding: 4px;
  margin-left: auto;
}

.collapse-btn-detail:hover {
  color: #409eff;
}

.conversation-messages {
  flex: 1;
  padding: 16px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 12px;
  background-image: url('~@/assets/conversation_bgd_gradient.jpeg');
  background-size: 100% auto;
  background-position: top center;
  background-repeat: repeat-y;
  min-height: 0;
}

.loading-more-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px;
  color: #909399;
  font-size: 14px;
}

.loading-more-indicator i {
  font-size: 16px;
}

.conversation-message-wrapper {
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  width: 100%;
  gap: 8px;
}

.conversation-message-wrapper.sent-by-me {
  justify-content: flex-end;
}

.message-avatar {
  flex-shrink: 0;
}

.conversation-message {
  max-width: 70%;
  padding: 10px 12px;
  background: #ffffff;
  border: 1px solid #e4e7ed;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.conversation-message:hover {
  background: #f5f7fa;
}

.conversations-container.right-panel-visible .conversation-message.selected {
  box-shadow: 0 0 0 2px #409eff;
}

.conversation-message-wrapper.sent-by-me .conversation-message {
  background: #e1f0ff;
  border: 1px solid #91d5ff;
  border-radius: 12px 12px 0 12px;
}

.conversation-message-wrapper:not(.sent-by-me) .conversation-message {
  background: #ffffff;
  border: 1px solid #e4e7ed;
  border-radius: 12px 12px 12px 0;
}

.conversation-message-wrapper.sent-by-me .conversation-message:hover {
  background: #d0e7ff;
}

.conversation-message-wrapper:not(.sent-by-me) .conversation-message:hover {
  background: #f5f7fa;
}

.conv-msg-content {
  font-size: 14px;
  color: #303133;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 5;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  word-wrap: break-word;
}

.conv-msg-time {
  font-size: 11px;
  color: #909399;
  margin-top: 2px;
}

.conversation-message-wrapper.sent-by-me .conv-msg-time {
  align-self: flex-end;
}

.conversation-message-wrapper:not(.sent-by-me) .conv-msg-time {
  align-self: flex-start;
}

.conversation-reply {
  padding: 16px;
  border-top: 1px solid #e4e7ed;
  display: flex;
  gap: 8px;
  flex-shrink: 0;
}

.conversation-reply .el-textarea {
  flex: 1;
}

/* Empty state */
.empty-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #ffffff;
  box-shadow:
    -4px 0 12px rgba(0, 0, 0, 0.08),
    4px 0 12px rgba(0, 0, 0, 0.08);
  z-index: 10;
  position: relative;
  border-left: 1px solid #c0c0c0;
  border-right: 1px solid #c0c0c0;
}

/* Panel overlay/backdrop */
.panel-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 998;
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

/* Floating toggle buttons */
.floating-sidebar-toggle {
  position: fixed;
  z-index: 1000;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transition: all 0.3s ease;
}

.floating-sidebar-toggle.left {
  left: 20px;
  top: 50%;
  transform: translateY(-50%);
}

.floating-sidebar-toggle.left:hover {
  transform: translateY(-50%) scale(1.1);
}

.floating-sidebar-toggle.right {
  right: 20px;
  top: 50%;
  transform: translateY(-50%);
}

.floating-sidebar-toggle.right:hover {
  transform: translateY(-50%) scale(1.1);
}

/* Panel close button */
.panel-close-btn {
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 100;
  background: white;
  border: 1px solid #dcdfe6;
}

.panel-close-btn:hover {
  background: #f5f7fa;
}

/* Force show panels */
.conversations-sidebar.force-show,
.right-panel.force-show {
  display: flex !important;
  position: fixed !important;
  height: 100vh;
  top: 0;
  z-index: 999;
  box-shadow: 0 0 20px rgba(0, 0, 0, 0.2);
  background: white;
  animation: slideIn 0.3s ease;
}

.conversations-sidebar.force-show {
  left: 0;
}

.right-panel.force-show {
  right: 0;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateX(-20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

.right-panel.force-show {
  animation: slideInRight 0.3s ease;
}

@keyframes slideInRight {
  from {
    opacity: 0;
    transform: translateX(20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

/* Responsive */
@media (max-width: 1399px) and (min-width: 1200px) {
  .right-panel {
    width: 350px !important;
    max-width: 350px !important;
  }
}

@media (max-width: 1199px) and (min-width: 1100px) {
  .right-panel {
    width: 300px !important;
    max-width: 300px !important;
  }
}

@media (max-width: 1099px) and (min-width: 1000px) {
  .right-panel {
    width: 300px !important;
    max-width: 300px !important;
  }

  .conversations-sidebar {
    width: 280px !important;
  }
}

@media (max-width: 999px) and (min-width: 850px) {
  .right-panel {
    display: none !important;
  }

  .conversations-sidebar {
    width: 300px !important;
  }

  .middle-panel {
    min-width: 480px;
  }
}

@media (max-width: 849px) and (min-width: 780px) {
  .right-panel {
    display: none !important;
  }

  .conversations-sidebar {
    width: 250px !important;
    max-width: 250px !important;
    min-width: 250px !important;
  }

  .middle-panel {
    flex: 1;
    min-width: 480px;
  }
}

@media (max-width: 779px) and (min-width: 500px) {
  .right-panel,
  .conversations-sidebar {
    display: none !important;
  }

  .middle-panel {
    width: 100% !important;
    max-width: 100% !important;
  }

  .content-panel {
    width: 100% !important;
  }

  .card-share-content {
    max-width: 400px;
    margin: 0 auto;
    padding: 0 20px;
  }
}

@media (max-width: 499px) {
  .conversations-container {
    flex-direction: column;
  }

  .right-panel,
  .conversations-sidebar {
    display: none !important;
  }

  .middle-panel,
  .content-panel {
    width: 100% !important;
    max-width: 100% !important;
    min-width: 100% !important;
  }

  .message-detail-header {
    padding: 12px;
  }

  .message-detail-content {
    padding: 12px;
  }

  .card-share-content {
    max-width: 100%;
    padding: 0 12px;
  }

  .message-content-text {
    font-size: 14px;
  }
}
</style>
